import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://juliusmason.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-cover.jpg`;

/**
 * Drop-in SEO tags. Every page renders this once near the top.
 * Passes rich schema.org JSON-LD when provided. `path` is required to build
 * canonical + hreflang alternates correctly.
 *
 *   <SEO
 *     title="Marketing digital à Toulouse | Julius Mason"
 *     description="…"
 *     path="/marketing-digital-toulouse"
 *     lang="fr"
 *     schema={{ "@context": "https://schema.org", "@type": "Course", ... }}
 *   />
 */
export default function SEO({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  lang = "en",
  keywords,
  schema,
  noindex = false,
}) {
  const base = "Julius Mason — Professional Websites for Businesses Worldwide";
  const fullTitle = title
    ? (title.includes("Julius Mason") ? title : `${title} | Julius Mason`)
    : base;
  const url = `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;

  // Support multiple schemas passed as an array.
  const schemas = Array.isArray(schema) ? schema : (schema ? [schema] : []);

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Julius Mason" />
      <meta property="og:locale" content={lang === "fr" ? "fr_FR" : "en_US"} />
      <meta property="og:locale:alternate" content={lang === "fr" ? "en_US" : "fr_FR"} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@juliusmason" />
      <meta name="twitter:creator" content="@juliusmason" />

      {/* hreflang — EN default; FR via the on-site language switcher */}
      <link rel="alternate" hrefLang="en" href={url} />
      <link rel="alternate" hrefLang="fr" href={`${url}${url.includes("?") ? "&" : "?"}lang=fr`} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}

/* --- schema helpers ------------------------------------------------------ */
export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export const articleSchema = ({ title, description, image, datePublished, dateModified, url, author = "Julius Mason" }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title,
  description,
  image: image ? [image] : undefined,
  datePublished,
  dateModified: dateModified || datePublished,
  author: { "@type": "Person", name: author, url: SITE_URL },
  publisher: {
    "@type": "Organization",
    name: "Julius Mason",
    logo: { "@type": "ImageObject", url: `${SITE_URL}/og-cover.jpg` },
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": url },
});

export const courseSchema = ({ name, description, url, provider = "Mason Academy" }) => ({
  "@context": "https://schema.org",
  "@type": "Course",
  name,
  description,
  provider: {
    "@type": "Organization",
    name: provider,
    sameAs: SITE_URL,
  },
  url,
});
